import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { CommunicatorService } from 'src/app/common/services/communicator.service';
import { API_CURRENT_VERSION, EndPointBaseUrl, ExtensionRoles } from 'src/app/common/settings/settings';
import { IAthenaExtensionValues, IAthenaValues } from '../types/athenaValues';
import {environment} from "../../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ExtensionService {

  private scrappedData = new ReplaySubject<IAthenaExtensionValues>(null)
  private extensionError = new BehaviorSubject<boolean>(false)
  requestDataSent: boolean = false

  constructor(private communicatorService: CommunicatorService) { }



  private __sendMessage<T>(message: {type: string, value?: T}) {
    const messageWithVersion = {
      ...message,
      version: 1
    }
    window.parent.postMessage(messageWithVersion, environment.athenaEhrUrl);
  }

  getMemberId$(patientId: number, projectId: number, clientId: number) {
    return this.communicatorService.getData(EndPointBaseUrl + API_CURRENT_VERSION + "/members/get_member_detail_by_patient_id/", {
      patient_id: patientId, projectId, clientId
    });
  }

  async getMemberId(patient_id: number, projectId: number, clientId: number) {
    return await this.communicatorService.getData(EndPointBaseUrl + API_CURRENT_VERSION + '/members/get_member_detail_by_patient_id/', { patient_id, projectId, clientId }).toPromise()
  }

  async getEHRRole(ehr_practice_id: number) {
    return await this.communicatorService.getData(EndPointBaseUrl + API_CURRENT_VERSION + '/projects/get_ehr_role/', { ehr_practice_id }).toPromise()
  }

  publishScrappedData(dataFromExt: IAthenaExtensionValues) {
    this.scrappedData.next(dataFromExt)
  }

  subscribeToScrappedData() {
    return this.scrappedData.asObservable()
  }

  sendRiskGapsToExt(numberOfRiskGaps: number) {
    const message = ({
      type: 'risk-gap',
      value: numberOfRiskGaps,
    });

    if(!isNaN(numberOfRiskGaps)) this.__sendMessage(message);
  }

  sendQueriesToExt(numberOfQueries: number) {
    const message = ({
      type: 'queries',
      value: numberOfQueries,
    });
    this.__sendMessage(message);
  }

  sendCompleteStatusToExt() {
    const message = {
      type: 'risk-gap-completed'
    }
    this.__sendMessage(message);
  }

  sendPatientNotFound() {
    const message = {
      type: 'user-not-found'
    }
    this.__sendMessage(message);
  }

  sendUserLogout() {
    const message = {
      type: 'user-logout'
    }
    this.__sendMessage(message);
  }

  sendCdiCompleteStatusToExt(){
    const message = {
      type: 'cdi-risk-gap-completed'
    }
    this.__sendMessage(message);
  }

  sendIsProviderStatusToExt(is_provider: boolean){
    const message = ({
      type: 'set-role',
      value: {
        is_provider
      }
    });
    this.__sendMessage(message);
  }

  sendRoleToExt(roleId: keyof typeof ExtensionRoles){
    const message = {
      type: 'set-user-role',
      value: {
        role: ExtensionRoles[roleId]
      }
    }
    this.__sendMessage(message);
  }

  sendProviderRiskGapSubmitted(){
    const message = ({
      type: 'risk-gap-submitted'
    })
    this.__sendMessage(message);
  }

  sendCDIRiskGapSubmitted(){
    const message = ({
      type: 'cdi-risk-gap-submitted'
    })
    this.__sendMessage(message);
  }

  sendRouteMessage(patientId: number){
    const message = {
      type: 'route-to-chart',
      value: patientId
    }
    this.__sendMessage(message);
  }

  sendCdiReportLoadComplete(){
    const message = {
      type: 'cdi-report-loaded'
    }
    this.__sendMessage(message);
  }

  sendErrorToExt(){
    const message = {
      type: 'application-error'
    }
    this.__sendMessage(message);
  }

  setExtensionError(value: boolean):void{
    this.extensionError.next(value)
  }

  getExtensionError(): Observable<boolean>{
    return this.extensionError.asObservable();
  }

  removeLoadingOverlay(): void{
    const message = {
      type: "remove-loading-overlay"
    };
    this.__sendMessage(message);
  }

  requestForScrapedData(){
    const message = {
      type: 'request-scraped-data'
    }
    !this.requestDataSent && this.__sendMessage(message);
    this.requestDataSent = true
  }

}
